import { useTranslation } from 'react-i18next';

export interface EvidenceMessage {
  id: string;
  sender_name: string;
  content: string;
  timestamp: string;
  chat_name?: string;
  key_phrases?: string[];
}

interface EvidencePanelProps {
  messages: EvidenceMessage[];
  explanation: string;
  isOpen: boolean;
  onToggle: () => void;
}

function highlightPhrases(text: string, phrases: string[]): React.ReactNode[] {
  if (!phrases || phrases.length === 0) return [text];
  const escaped = phrases.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) => {
    const isMatch = phrases.some(
      (p) => p.toLowerCase() === part.toLowerCase(),
    );
    if (isMatch) {
      return (
        <mark key={i} className="bg-yellow-200 font-semibold rounded px-0.5">
          {part}
        </mark>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

function formatTime(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    // Try parsing as numeric timestamp if ISO failed
    const num = Number(dateStr);
    if (!isNaN(num)) {
      const d = new Date(num > 1e12 ? num : num * 1000);
      if (!isNaN(d.getTime())) {
        return d.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    }
    return '';
  }
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function EvidencePanel({
  messages,
  explanation,
  isOpen,
  onToggle,
}: EvidencePanelProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
      {/* Toggle header */}
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
      >
        <span className="flex items-center gap-2">
          {'🔍'} {t('signals.evidence')}
          <span className="text-xs text-gray-400">
            ({messages.length} {t('signals.viewMessages').toLowerCase()})
          </span>
        </span>
        <span
          className={`transform transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          ▾
        </span>
      </button>

      {/* Collapsible body */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="border-t border-gray-200 px-4 py-3 space-y-3">
          {/* Explanation */}
          {explanation && (
            <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-800">
              {explanation}
            </div>
          )}

          {/* Message list */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto scrollbar-thin">
            {[...messages]
              .sort(
                (a, b) =>
                  new Date(a.timestamp).getTime() -
                  new Date(b.timestamp).getTime(),
              )
              .map((msg) => (
                <div
                  key={msg.id}
                  className="rounded-md bg-white border border-gray-200 px-3 py-2"
                >
                  <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-gray-900">
                    {msg.sender_name}
                  </span>
                  <span className="text-[11px] text-gray-400">
                    {formatTime(msg.timestamp)}
                  </span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {highlightPhrases(msg.content, msg.key_phrases ?? [])}
                </p>
                {msg.chat_name && (
                  <span className="mt-1 inline-block text-[11px] text-gray-400">
                    {'💬'} {msg.chat_name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
